import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { EMPTY, Subject, catchError, filter, switchMap, take, takeUntil } from 'rxjs';
import { AuthService } from '../../../../services/auth.service';
import {
  CapiDailyOutcomeDto,
  CapiErrorGroupDto,
  CapiHealthReportDto,
  CapiHealthStatus,
  MarketingAcquisitionService
} from '../../../../services/marketing-acquisition.service';
import { ChartWrapperComponent } from '../../../charts/shared/chart-wrapper/chart-wrapper.component';
import { EntityChartDataDto } from '../../../../models/chart.models';
import type { AcquisitionDateRange } from '../acquisition.component';
import { Chart, registerables } from 'chart.js';

// Same as cumulative-charts / daily-glance: don't rely on another component having registered
// Chart.js first (re-registering is a no-op).
Chart.register(...registerables);

interface HealthBanner {
  title: string;
  detail: string;
  classes: string;
  icon: string;
}

/** Plain-language explanations for Meta error subcodes seen in production. */
const KNOWN_META_ERRORS: Record<number, string> = {
  2804066: 'Event name not allowed for WhatsApp events. Lead must be sent as LeadSubmitted (fixed in the attribution release).',
  2804132: 'The CAPI dataset is not linked to the WhatsApp Business Account. Link it in Meta Events Manager.'
};

const ROUTE_LABELS: Record<string, string> = {
  business_messaging: 'WhatsApp (click-to-chat ads)',
  website: 'Website',
  system_generated: 'System (pickups, app bookings)'
};

/**
 * V5 — Meta CAPI delivery health: is Meta receiving our conversion events, by route, and if not,
 * why (Meta's error codes)?
 */
@Component({
  selector: 'app-capi-health',
  standalone: true,
  imports: [CommonModule, ChartWrapperComponent],
  templateUrl: './capi-health.component.html'
})
export class CapiHealthComponent implements OnInit, OnChanges, OnDestroy {
  @Input({ required: true }) range!: AcquisitionDateRange;

  loading = false;
  errorMessage = '';
  report: CapiHealthReportDto | null = null;
  banner: HealthBanner | null = null;
  sentChart: EntityChartDataDto | null = null;
  failedChart: EntityChartDataDto | null = null;

  private readonly load$ = new Subject<AcquisitionDateRange>();
  private readonly destroy$ = new Subject<void>();

  constructor(
    private acquisitionService: MarketingAcquisitionService,
    private authService: AuthService
  ) { }

  ngOnInit(): void {
    // switchMap drops a slower earlier response if the range changes again mid-request. Errors are
    // caught per request so one failure doesn't end the stream and the next Apply still loads.
    this.load$
      .pipe(
        switchMap(range => {
          this.loading = true;
          this.errorMessage = '';
          return this.acquisitionService.getCapiHealth(range.from, range.to).pipe(
            catchError(error => {
              this.onError(error);
              return EMPTY;
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe(report => this.onReport(report));

    this.authService.getMsalInitializedObservable()
      .pipe(filter(initialized => initialized), take(1), takeUntil(this.destroy$))
      .subscribe(() => this.load$.next(this.range));
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['range'] && !changes['range'].firstChange) {
      this.load$.next(this.range);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  routeLabel(actionSource: string): string {
    return ROUTE_LABELS[actionSource] ?? actionSource;
  }

  errorHint(error: CapiErrorGroupDto): string | null {
    if (error.status === 'Error') {
      return 'Network or server failure that kept failing after 5 retries.';
    }
    return error.errorSubcode != null ? KNOWN_META_ERRORS[error.errorSubcode] ?? null : null;
  }

  private onReport(report: CapiHealthReportDto): void {
    this.report = report;
    this.banner = this.toBanner(report.health);
    this.sentChart = this.toChart('Accepted by Meta', report.daily, d => d.sent);
    this.failedChart = this.toChart('Rejected or failed', report.daily, d => d.rejected + d.error);
    this.loading = false;
  }

  private onError(error: { status?: number; error?: { error?: string } }): void {
    this.loading = false;
    this.report = null;
    if (error.status === 400 && error.error?.error) {
      this.errorMessage = error.error.error;
    } else if (error.status === 403) {
      this.errorMessage = 'Access denied. You do not have permission to view marketing reports.';
    } else {
      this.errorMessage = 'Failed to load delivery health. Please try again.';
    }
  }

  private toChart(name: string, days: CapiDailyOutcomeDto[], value: (d: CapiDailyOutcomeDto) => number): EntityChartDataDto {
    return {
      entityName: name,
      totalCount: days.reduce((sum, d) => sum + value(d), 0),
      dataPoints: days.map(d => ({ label: d.date, value: value(d) }))
    };
  }

  private toBanner(health: CapiHealthStatus): HealthBanner {
    switch (health) {
      case 'Healthy':
        return { title: 'Healthy', detail: 'Meta is accepting our conversion events.', classes: 'bg-green-50 border-green-200 text-green-800', icon: 'fas fa-check-circle' };
      case 'Degraded':
        return { title: 'Degraded', detail: 'Most events are accepted, but more than 5% are rejected or failing. Check the errors below.', classes: 'bg-amber-50 border-amber-200 text-amber-800', icon: 'fas fa-exclamation-triangle' };
      case 'Failing':
        return { title: 'Failing', detail: 'Events are reaching Meta but none are accepted, so ad optimisation gets no conversion data. Check the errors below.', classes: 'bg-red-50 border-red-200 text-red-800', icon: 'fas fa-times-circle' };
      case 'Off':
        return { title: 'Sending is switched off', detail: 'Every event was skipped by the CAPI kill switch (MetaMarketingSettings__CapiSendEnabled = false).', classes: 'bg-gray-50 border-gray-200 text-gray-700', icon: 'fas fa-power-off' };
      default:
        return { title: 'No send attempts', detail: 'No conversion was sent to Meta in this period.', classes: 'bg-gray-50 border-gray-200 text-gray-700', icon: 'fas fa-info-circle' };
    }
  }
}
